import {
	Box,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Button,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
} from '@mui/material';
import { generateClient } from 'aws-amplify/api';
import moment from 'moment';
import { enqueueSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import { scheduledBulkOperationRunsByOperation } from '../../graphql/queries';
import { runStatusColors } from './bulkScheduleDialog';

/**
 * Most recent runs retrieved and displayed for one operation.
 *
 * This is the single source of the cap: it supplies the query's `limit` and the
 * number in {@link RUN_HISTORY_CAP_CAPTION}, so the figure shown to the user
 * cannot drift from the figure actually requested.
 */
export const RUN_HISTORY_LIMIT = 100;

/**
 * Rendered with the table whenever a history is displayed. The capped read
 * cannot tell whether an older run exists, so the statement is unconditional
 * rather than shown only when the cap is reached.
 */
export const RUN_HISTORY_CAP_CAPTION = `Showing the ${RUN_HISTORY_LIMIT} most recent runs. Older runs are not displayed.`;

/** Shown when the operation has no run records at all. */
export const NO_RUNS_MESSAGE = 'This operation has not run yet.';

/** Shown (and snackbarred) when the run history fetch fails. */
export const LOAD_FAILED_MESSAGE = 'Unable to load run history';

/** Marker placed next to the status of a run that hit its time budget. */
export const TRUNCATED_MARKER = 'stopped early';

/**
 * Orders run records newest first.
 *
 * The index already returns descending `startedAt`, but sorting here makes the
 * display order a property of this component rather than only of the index.
 *
 * @param {Array<object>} runs
 * @returns {Array<object>} A new array, newest `startedAt` first.
 */
function sortRunsNewestFirst(runs) {
	return [...runs].sort((a, b) => {
		const left = a?.startedAt ?? '';
		const right = b?.startedAt ?? '';

		if (left === right) {
			return 0;
		}

		return left < right ? 1 : -1;
	});
}

/**
 * Read-only run history for one scheduled bulk operation.
 *
 * The read is intentionally capped at the {@link RUN_HISTORY_LIMIT} most recent
 * attempts, so `fetchAllPages` is deliberately not used here — paging every run
 * of a long-lived daily operation would be a large read to render a list the UI
 * truncates anyway.
 *
 * The `tenant` filter is defense in depth rather than a selective clause: every
 * run under an `operationId` already shares that operation's tenant, so the
 * DynamoDB "filter applies after limit" behavior does not produce short pages
 * in practice.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {object|null} props.operation The ScheduledBulkOperation whose runs to show.
 * @param {string} props.tenantId Tenant of the signed-in user.
 * @param {() => void} props.onClose
 * @category Components
 * @component
 */
export function RunHistoryDialog({ open, operation, tenantId, onClose }) {
	// One client for the component's lifetime; recreating it per render would
	// also re-trigger the fetch effect.
	const client = useMemo(() => generateClient(), []);
	const [runs, setRuns] = useState([]);
	const [loading, setLoading] = useState(false);
	const [failed, setFailed] = useState(false);

	const operationId = operation?.id;

	useEffect(() => {
		// `active` drops the response of a superseded operation so a slow fetch
		// cannot paint another operation's runs.
		let active = true;

		if (!open || !operationId) {
			setRuns([]);
			setLoading(false);
			setFailed(false);
			return undefined;
		}

		async function loadRuns() {
			setLoading(true);
			setFailed(false);

			try {
				const result = await client.graphql({
					query: scheduledBulkOperationRunsByOperation,
					variables: {
						operationId,
						sortDirection: 'DESC',
						limit: RUN_HISTORY_LIMIT,
						filter: { tenant: { eq: tenantId } },
					},
				});

				if (!active) return;

				const items =
					result?.data?.scheduledBulkOperationRunsByOperation?.items ?? [];
				setRuns(sortRunsNewestFirst(items.filter(Boolean)));
			} catch (err) {
				if (!active) return;

				console.error(
					'Error loading scheduled bulk operation run history',
					err,
				);
				enqueueSnackbar(LOAD_FAILED_MESSAGE, { variant: 'error' });
				setRuns([]);
				setFailed(true);
			} finally {
				if (active) {
					setLoading(false);
				}
			}
		}

		loadRuns();

		return () => {
			active = false;
		};
	}, [open, operationId, tenantId, client]);

	return (
		<Dialog maxWidth="lg" fullWidth open={open} onClose={onClose}>
			<DialogTitle>
				Run History
				{operation?.name ? ` — ${operation.name}` : ''}
			</DialogTitle>
			<DialogContent>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: 1,
						paddingTop: 2,
					}}
				>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Started</TableCell>
								<TableCell>Status</TableCell>
								<TableCell align="right">Matched</TableCell>
								<TableCell align="right">Processed</TableCell>
								<TableCell align="right">Errors</TableCell>
								<TableCell>Message</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{loading && (
								<TableRow>
									<TableCell colSpan={6}>
										<CircularProgress
											aria-label="Loading run history"
											variant="indeterminate"
										/>
									</TableCell>
								</TableRow>
							)}
							{!loading && failed && (
								<TableRow>
									<TableCell colSpan={6}>
										<Typography variant="body2" color="textSecondary">
											{LOAD_FAILED_MESSAGE}
										</Typography>
									</TableCell>
								</TableRow>
							)}
							{!loading && !failed && runs.length === 0 && (
								<TableRow>
									<TableCell colSpan={6}>
										<Typography variant="body2" color="textSecondary">
											{NO_RUNS_MESSAGE}
										</Typography>
									</TableCell>
								</TableRow>
							)}
							{!loading &&
								!failed &&
								runs.map((run) => (
									<TableRow key={run.id}>
										<TableCell>
											{run.startedAt
												? moment(run.startedAt).format('MM/DD/YYYY hh:mm A')
												: '--'}
										</TableCell>
										<TableCell>
											<Box
												sx={{
													display: 'flex',
													alignItems: 'center',
													gap: 1,
												}}
											>
												<Chip
													size="small"
													label={run.status ?? 'Unknown'}
													color={runStatusColors[run.status] ?? 'default'}
												/>
												{run.truncated && (
													<Typography variant="caption" color="textSecondary">
														{TRUNCATED_MARKER}
													</Typography>
												)}
											</Box>
										</TableCell>
										<TableCell align="right">{run.matched ?? 0}</TableCell>
										<TableCell align="right">{run.processed ?? 0}</TableCell>
										<TableCell align="right">{run.errors ?? 0}</TableCell>
										<TableCell>
											<Typography variant="body2">
												{run.message || '--'}
											</Typography>
										</TableCell>
									</TableRow>
								))}
						</TableBody>
					</Table>

					{/* Outside the table body so it accompanies the list in every state
					    where a history is displayed, loading and empty included. */}
					<Typography variant="caption" color="textSecondary">
						{RUN_HISTORY_CAP_CAPTION}
					</Typography>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button variant="secondary" onClick={onClose}>
					Close
				</Button>
			</DialogActions>
		</Dialog>
	);
}
